// ProcessTimeline.js - Australia
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Skilled Independent (189)',
      stages: [
        { step: 'Skills Assessment', duration: '2-4 months', description: 'Secure a positive assessment from the authority designated for your occupation' },
        { step: 'Lodge Your EOI in SkillSelect', duration: '1-2 weeks', description: 'Build the Expression of Interest and file it with the supporting detail' },
        { step: 'Wait for an Invitation', duration: 'Varies', description: 'Invitations follow your points score and occupation, and can take 1-12+ months' },
        { step: 'Lodge Visa Application', duration: '60 days', description: 'The full application has to be filed within 60 days of the invitation' },
        { step: 'Departmental Assessment', duration: '6-12 months', description: 'Your case is worked through, health and character checks included' },
        { step: 'Visa Grant', duration: '1-2 weeks', description: 'The grant notice arrives and you make your first entry to Australia' }
      ],
      totalTime: '12-24 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'State Nominated (190/491)',
      stages: [
        { step: 'Skills Assessment', duration: '2-4 months', description: 'Obtain a positive assessment from the designated authority' },
        { step: 'Apply for Nomination', duration: '2-12 weeks', description: 'Lodge with your chosen state or territory, each of which runs its own process' },
        { step: 'Nomination Decision', duration: 'Varies by state', description: 'The state examines your case and approves the nomination' },
        { step: 'EOI and Invitation', duration: '1-4 weeks', description: 'File the EOI carrying your nomination, then receive the invitation to apply' },
        { step: 'Lodge Visa Application', duration: '60 days', description: 'Submit the visa application together with the complete document set' },
        { step: 'Departmental Assessment', duration: '8-12 months', description: 'The Department works through your visa application' },
        { step: 'Visa Grant', duration: '1-2 weeks', description: 'The visa is granted and you can start planning the move' }
      ],
      totalTime: '12-18 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Employer Sponsored (186)',
      stages: [
        { step: 'Find a Sponsoring Employer', duration: 'Varies', description: 'Land an offer from an Australian employer approved to sponsor workers' },
        { step: 'Skills Assessment', duration: '2-4 months', description: 'Have your skills assessed against the occupation being nominated' },
        { step: 'Employer Nomination', duration: '1-3 months', description: 'Your employer files its nomination application with the Department' },
        { step: 'Lodge Visa Application', duration: 'After nomination', description: 'Once the nomination is approved, the visa application goes in' },
        { step: 'Departmental Assessment', duration: '6-9 months', description: 'Assessment proceeds alongside the standard checks' },
        { step: 'Visa Grant', duration: '1-2 weeks', description: 'The grant comes through and you can begin work' }
      ],
      totalTime: '12-18 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            How Long Each Route Takes
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Knowing roughly how long a pathway runs makes it far easier to plan work, housing and family arrangements around it.
          </p>
        </motion.div>

        {/* Timelines */}
        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              {/* Timeline Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Total Processing Time: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              {/* Timeline Steps */}
              <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Steps */}
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      {/* Step Number */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>

                      {/* Step Content */}
                      <div className="bg-surface p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-primary">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-secondary bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              What Slows Things Down
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Gaps or mistakes in the documents you submit</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Assessing authorities each work at their own pace</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Nomination turnaround differs widely between jurisdictions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Case volumes and priority settings inside the Department</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              Where We Come In
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Checking the file is complete before anything is lodged</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Walking you through the skills assessment step</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Recommending which state nomination to pursue</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Tracking the case and answering requests without delay</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
